// Shared helper for registration month/period navigation.
// One rule set for Paid Registrations, Interest Forms, and any future
// registration surface. See RegistrationsPanel for consumption.
//
// Rules:
// - Primary tabs are semester-first and fixed:
//   Fall Semester 2026 · Winter Semester 2026–2027 · Interest Forms ·
//   Needs Resolution (plus any other actionable review bucket with records).
// - Calendar-month periods (May–August 2026) are archival and appear in the
//   "Archived / Historical" dropdown, newest first. Nothing is deleted or
//   hidden from access.
// - Timezone: America/Los_Angeles (Silver Sage).


import type { EnrollmentPeriod } from "./enrollment";
import { ACTIONABLE_BUCKETS, REVIEW_BUCKETS, type ReviewBucket } from "./registrationSession";

/**
 * A tab key is either a real period or an explicit review bucket.
 * The vague "Unspecified" bucket is retired — records land in a named
 * review bucket that states exactly what is missing.
 */
export type RegistrationTabKey =
  | EnrollmentPeriod
  | ReviewBucket
  | typeof INTEREST_TAB
  | typeof WINTER_TAB
  | typeof OCTOBER_TAB;

/** Interest forms live in the SAME primary strip — no separate toggle. */
export const INTEREST_TAB = "Interest Forms";

/**
 * Presentation-only tab for the next academic term. It is NOT a canonical
 * enrollment period: no registration is ever routed here automatically.
 * When Winter offerings are configured, their records resolve through the
 * canonical resolver like every other period.
 */
export const WINTER_TAB = "Winter Semester 2026–2027";

/**
 * Presentation-only tab for the separate October month-only offerings.
 * Records land here only when their purchased session label is an
 * October-only offering (e.g. "October", "October Session") — never by
 * registration/payment date, and never when the label names a semester.
 */
export const OCTOBER_TAB = "October 2026";

/** True when a purchased session label is an October month-only offering. */
export function isOctoberOnlyLabel(label: string | null | undefined): boolean {
  const l = String(label ?? "");
  return /\boctober\b/i.test(l) && !/\b(fall|spring|semester)\b/i.test(l);
}

/** Display labels for tabs whose internal key is terser than the UI text. */
const TAB_LABELS: Partial<Record<RegistrationTabKey, string>> = {
  "Fall Semester": "Fall Semester 2026",
  "Spring Semester": "Spring Semester 2027",
};

export function registrationTabLabel(key: RegistrationTabKey): string {
  return TAB_LABELS[key] ?? String(key);
}


export interface PeriodCount {
  period: RegistrationTabKey;
  count: number;
}

export interface PastPeriodItem {
  period: RegistrationTabKey;
  label: string; // e.g. "July 2026"
  count: number;
}

export interface RegistrationPeriodNav {
  main: PeriodCount[];
  /** Collapsed "Historical" group — delivered sessions and legacy records. */
  past: PastPeriodItem[];
  /** Collapsed "Test Records" group. */
  tests: PastPeriodItem[];
  defaultPeriod: RegistrationTabKey | null;
}

const SUMMER: Record<string, number> = {
  January: 0, February: 1, March: 2, April: 3, May: 4, June: 5,
  July: 6, August: 7, September: 8, October: 9, November: 10, December: 11,
};
const SUMMER_PERIODS: EnrollmentPeriod[] = ["May", "June", "July", "August"];


function nowInTZ(tz: string, at: Date): { month: number; year: number } {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    year: "numeric",
    month: "numeric",
  }).formatToParts(at);
  const m = Number(parts.find((p) => p.type === "month")?.value ?? "1") - 1;
  const y = Number(parts.find((p) => p.type === "year")?.value ?? "2026");
  return { month: m, year: y };
}

function currentSummerPeriod(monthIdx: number): EnrollmentPeriod | null {
  const map: Record<number, EnrollmentPeriod> = { 4: "May", 5: "June", 6: "July", 7: "August" };
  return map[monthIdx] ?? null;
}

function activeSemester(monthIdx: number): EnrollmentPeriod | null {
  if (monthIdx >= 8) return "Fall Semester"; // Sep–Dec
  if (monthIdx <= 4) return "Spring Semester"; // Jan–May
  return null;
}

/**
 * Build the primary navigation + Historical dropdown.
 *
 * PRIMARY (fixed order):
 *   1. Fall Semester 2026
 *   2. Winter Semester 2026–2027   (view only until offerings exist)
 *   3. Interest Forms
 *   4. Needs Resolution
 *   …any other actionable review bucket that currently has records.
 *
 * Every calendar-month period (May–August) is archival and lives in the
 * Historical dropdown. Nothing is filtered out of existence — only moved.
 */
export function getRegistrationPeriodNavigation(
  counts: PeriodCount[],
  currentDate: Date = new Date(),
  timezone: string = "America/Los_Angeles",
): RegistrationPeriodNav {
  const { year: curYear } = nowInTZ(timezone, currentDate);
  const byPeriod = new Map<RegistrationTabKey, number>();
  for (const c of counts) byPeriod.set(c.period, (byPeriod.get(c.period) ?? 0) + c.count);

  const main: PeriodCount[] = [];
  const past: PastPeriodItem[] = [];
  const tests: PastPeriodItem[] = [];
  const usedInMain = new Set<RegistrationTabKey>();

  const pushMain = (period: RegistrationTabKey, count: number) => {
    main.push({ period, count });
    usedInMain.add(period);
  };

  // 1) Fall Semester 2026 — always first, even at zero.
  pushMain("Fall Semester", byPeriod.get("Fall Semester") ?? 0);

  // 1b) October 2026 — separate month-only offerings.
  pushMain(OCTOBER_TAB, byPeriod.get(OCTOBER_TAB) ?? 0);

  // 2) Winter Semester 2026–2027 — always present as an (empty) view.
  pushMain(WINTER_TAB, byPeriod.get(WINTER_TAB) ?? 0);

  // 3) Interest Forms.
  pushMain(INTEREST_TAB, byPeriod.get(INTEREST_TAB) ?? 0);

  // 4) Needs Resolution.
  pushMain("Needs Resolution", byPeriod.get("Needs Resolution") ?? 0);

  // Monthly periods are archival — Historical dropdown, never primary.
  for (const p of SUMMER_PERIODS) {
    const cnt = byPeriod.get(p) ?? 0;
    if (cnt > 0) past.push({ period: p, label: `${p} ${curYear}`, count: cnt });
  }

  // Spring Semester stays out of the primary strip until it carries records.
  const springCnt = byPeriod.get("Spring Semester") ?? 0;
  if (springCnt > 0) {
    past.push({ period: "Spring Semester", label: "Spring Semester 2027", count: springCnt });
  }

  // Review buckets.
  const legacyUnspecified = byPeriod.get("Unspecified") ?? 0;
  if (legacyUnspecified > 0) {
    byPeriod.set(
      "Needs Session Assignment",
      (byPeriod.get("Needs Session Assignment") ?? 0) + legacyUnspecified,
    );
  }
  for (const bucket of REVIEW_BUCKETS) {
    if (usedInMain.has(bucket)) continue;
    const cnt = byPeriod.get(bucket) ?? 0;
    if (cnt <= 0) continue;
    if (bucket === "Test Registration") {
      tests.push({ period: bucket, label: "Test records", count: cnt });
    } else if (bucket === "Historical") {
      past.push({ period: bucket, label: "Earlier / no longer offered", count: cnt });
    } else if ((ACTIONABLE_BUCKETS as string[]).includes(bucket)) {
      pushMain(bucket, cnt);
    }
  }

  // Historical dropdown: months reverse-chronological, named buckets last.
  past.sort((a, b) => {
    const ai = SUMMER[a.period] ?? -1;
    const bi = SUMMER[b.period] ?? -1;
    return bi - ai;
  });

  return { main, past, tests, defaultPeriod: "Fall Semester" };
}


/**
 * Subscribe a callback to local-midnight rollover in the given timezone.
 * Fires once when the calendar day changes so the caller can rebuild the
 * navigation without a manual refresh. Returns a cleanup function.
 */
export function subscribeToDayRollover(
  callback: () => void,
  timezone: string = "America/Los_Angeles",
): () => void {
  let lastKey = dayKey(new Date(), timezone);
  const interval = window.setInterval(() => {
    const k = dayKey(new Date(), timezone);
    if (k !== lastKey) {
      lastKey = k;
      callback();
    }
  }, 60_000);
  return () => window.clearInterval(interval);
}

function dayKey(at: Date, tz: string): string {
  const p = new Intl.DateTimeFormat("en-US", {
    timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(at);
  const y = p.find((x) => x.type === "year")?.value;
  const m = p.find((x) => x.type === "month")?.value;
  const d = p.find((x) => x.type === "day")?.value;
  return `${y}-${m}-${d}`;
}
